import React, { useState, useEffect } from 'react';
import { getMyReport } from '../../api';
import { toast } from 'react-toastify';

const WeeklyReport = () => {
  const [report, setReport] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadReport(); }, []);

  const loadReport = async () => {
    try {
      const res = await getMyReport();
      setReport(res.data);
    } catch (err) {
      toast.error('Error loading report');
    }
  };

  const getWeeklyData = () => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return report.filter(m => new Date(m.created_at) >= oneWeekAgo);
  };

  const getMonthlyData = () => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return report.filter(m => new Date(m.created_at) >= oneMonthAgo);
  };

  const getData = () => {
    if (filter === 'weekly') return getWeeklyData();
    if (filter === 'monthly') return getMonthlyData();
    return report;
  };

  const data = getData();

  const getGrade = (percentage) => {
    if (percentage >= 90) return { grade: 'A+', color: '#155724', bg: '#d4edda' };
    if (percentage >= 80) return { grade: 'A', color: '#155724', bg: '#d4edda' };
    if (percentage >= 70) return { grade: 'B', color: '#004085', bg: '#cce5ff' };
    if (percentage >= 60) return { grade: 'C', color: '#856404', bg: '#fff3cd' };
    if (percentage >= 50) return { grade: 'D', color: '#721c24', bg: '#f8d7da' };
    return { grade: 'F', color: '#721c24', bg: '#f8d7da' };
  };

  const totalObtained = data.reduce((sum, m) => sum + parseFloat(m.obtained_marks || 0), 0);
  const totalPossible = data.reduce((sum, m) => sum + parseFloat(m.total_marks || 0), 0);
  const overallPercentage = totalPossible > 0 ? ((totalObtained / totalPossible) * 100).toFixed(1) : 0;

  return (
    <div>
      <h3 style={styles.title}>📊 My Performance Report</h3>

      <div style={styles.filterRow}>
        {['all', 'weekly', 'monthly'].map(f => (
          <button key={f} style={{
            ...styles.filterBtn,
            ...(filter === f ? styles.filterBtnActive : {})
          }} onClick={() => setFilter(f)}>
            {f === 'all' ? '📋 All Time' : f === 'weekly' ? '📅 This Week' : '📆 This Month'}
          </button>
        ))}
      </div>

      {data.length > 0 && (
        <div style={styles.summaryCard}>
          <div style={styles.summaryItem}>
            <h2 style={styles.summaryNumber}>{data.length}</h2>
            <p style={styles.summaryLabel}>Total Exams</p>
          </div>
          <div style={styles.summaryItem}>
            <h2 style={styles.summaryNumber}>{totalObtained}</h2>
            <p style={styles.summaryLabel}>Total Obtained</p>
          </div>
          <div style={styles.summaryItem}>
            <h2 style={styles.summaryNumber}>{totalPossible}</h2>
            <p style={styles.summaryLabel}>Total Marks</p>
          </div>
          <div style={styles.summaryItem}>
            <h2 style={{
              ...styles.summaryNumber,
              color: overallPercentage >= 60 ? '#155724' : '#721c24'
            }}>{overallPercentage}%</h2>
            <p style={styles.summaryLabel}>Overall %</p>
          </div>
        </div>
      )}

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>Subject</th>
              <th style={styles.th}>Exam Type</th>
              <th style={styles.th}>Obtained</th>
              <th style={styles.th}>Total</th>
              <th style={styles.th}>Percentage</th>
              <th style={styles.th}>Grade</th>
              <th style={styles.th}>Date</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="7" style={styles.empty}>No data found!</td>
              </tr>
            )}
            {data.map(m => {
              const pct = ((m.obtained_marks / m.total_marks) * 100).toFixed(1);
              const gradeInfo = getGrade(pct);
              return (
                <tr key={m.id} style={styles.tableRow}>
                  <td style={styles.td}>{m.subject_name}</td>
                  <td style={styles.td}>
                    <span style={styles.typeBadge}>{m.exam_type}</span>
                  </td>
                  <td style={styles.td}>{m.obtained_marks}</td>
                  <td style={styles.td}>{m.total_marks}</td>
                  <td style={styles.td}>
                    <div style={styles.progressBar}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${pct}%`,
                        background: pct >= 60 ? '#28a745' : '#dc3545'
                      }} />
                      <span style={styles.progressText}>{pct}%</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.gradeBadge,
                      background: gradeInfo.bg,
                      color: gradeInfo.color
                    }}>
                      {gradeInfo.grade}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  title: { color: '#1a3a5c', marginBottom: '20px' },
  filterRow: { display: 'flex', gap: '10px', marginBottom: '20px' },
  filterBtn: { padding: '10px 20px', background: 'white', border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  filterBtnActive: { background: '#1a3a5c', color: 'white', borderColor: '#1a3a5c' },
  summaryCard: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' },
  summaryItem: { background: 'white', borderRadius: '12px', padding: '20px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  summaryNumber: { color: '#1a3a5c', margin: '0 0 5px', fontSize: '28px' },
  summaryLabel: { color: '#666', fontSize: '13px', margin: 0 },
  tableCard: { background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#1a3a5c' },
  th: { padding: '12px 15px', color: 'white', textAlign: 'left', fontSize: '13px' },
  tableRow: { borderBottom: '1px solid #eee' },
  td: { padding: '12px 15px', fontSize: '13px', color: '#444' },
  empty: { textAlign: 'center', color: '#999', padding: '40px' },
  typeBadge: { padding: '3px 10px', background: '#e8f0fe', color: '#1a3a5c', borderRadius: '20px', fontSize: '12px' },
  progressBar: { position: 'relative', background: '#e9ecef', borderRadius: '10px', height: '20px', width: '100px' },
  progressFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: '10px', transition: 'width 0.3s' },
  progressText: { position: 'absolute', width: '100%', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', color: 'white', lineHeight: '20px' },
  gradeBadge: { padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' },
};

export default WeeklyReport;